import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { useMapsLibrary } from "@vis.gl/react-google-maps";
import { CalendarIcon, Users, ArrowRight, Sparkles, Route, Plus, X, Clock, Mail, Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import AddressAutocomplete from "@/components/AddressAutocomplete";

const fieldCls = "bg-secondary border-white/10 text-foreground focus:ring-primary focus:border-primary rounded-none h-12";
const lbl = "text-xs uppercase tracking-[0.15em] text-muted-foreground";

export default function QuoteForm() {
  const navigate = useNavigate();
  const routesLib = useMapsLibrary("routes");
  const [vehicles, setVehicles] = useState([]);
  const [service, setService] = useState("transfer");
  const [form, setForm] = useState({
    pickup: "", dropoff: "", time: "10:00", passengers: "2", vehicle_type: "sedan",
    meet_greet: false, flight_number: "", promo_code: "", hours: "2",
  });
  const [pickupCoord, setPickupCoord] = useState(null);
  const [dropoffCoord, setDropoffCoord] = useState(null);
  const [stops, setStops] = useState([]); // {address, coord}
  const [date, setDate] = useState(null);
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [leadEmail, setLeadEmail] = useState("");
  const [emailed, setEmailed] = useState(false);
  const [tipPercent, setTipPercent] = useState(0);
  const [tipAmount, setTipAmount] = useState("");
  const [minFare, setMinFare] = useState(0);
  const [meetFee, setMeetFee] = useState(10);
  const [distCache, setDistCache] = useState({ miles: null, duration: null });

  useEffect(() => {
    api.get("/quote-options").then((r) => { setVehicles(r.data.vehicles); setMinFare(r.data.min_fare); setMeetFee(r.data.meet_greet_fee ?? 10); }).catch(() => {});
  }, []);

  const maxPax = vehicles.find((v) => v.id === form.vehicle_type)?.pax || 6;
  const changeVehicle = (v) => {
    const cap = vehicles.find((x) => x.id === v)?.pax || 6;
    setForm((f) => ({ ...f, vehicle_type: v, passengers: String(Math.min(Number(f.passengers), cap)) }));
  };

  const emailQuote = async () => {
    if (!leadEmail) { toast.error("Enter your email to receive the quote."); return; }
    try {
      await api.post("/quote/email", { quote_id: quote.id, email: leadEmail, origin_url: window.location.origin });
      setEmailed(true);
      toast.success("Quote sent to your inbox!");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const addStop = () => stops.length < 3 && setStops([...stops, { address: "", coord: null }]);
  const removeStop = (i) => setStops(stops.filter((_, idx) => idx !== i));
  const updateStop = (i, patch) => setStops(stops.map((s, idx) => idx === i ? { ...s, ...patch } : s));

  const getRouteDistance = async () => {
    if (!routesLib || !pickupCoord || !dropoffCoord) return { miles: null, duration: null };
    try {
      const svc = new routesLib.DirectionsService();
      const res = await svc.route({
        origin: pickupCoord, destination: dropoffCoord,
        waypoints: stops.filter((s) => s.coord).map((s) => ({ location: s.coord, stopover: true })),
        travelMode: window.google.maps.TravelMode.DRIVING,
      });
      const legs = res.routes[0].legs;
      const meters = legs.reduce((sum, l) => sum + l.distance.value, 0);
      const secs = legs.reduce((sum, l) => sum + l.duration.value, 0);
      return { miles: Math.round((meters / 1609.34) * 10) / 10, duration: `${Math.round(secs / 60)} min` };
    } catch { return { miles: null, duration: null }; }
  };

  const requote = async (dist, tipOpts, notifyOwner = false) => {
    const payload = {
      service_type: service, ...form, passengers: Number(form.passengers),
      date: format(date, "yyyy-MM-dd"), pickup_lat: pickupCoord.lat, pickup_lng: pickupCoord.lng,
      promo_code: form.promo_code, notify_owner: notifyOwner, ...tipOpts,
    };
    if (service === "transfer") {
      Object.assign(payload, {
        dropoff: form.dropoff, dropoff_lat: dropoffCoord.lat, dropoff_lng: dropoffCoord.lng,
        stops: stops.map((s) => s.address).filter(Boolean),
        distance_miles: dist.miles, duration_text: dist.duration,
      });
    } else {
      payload.hours = Number(form.hours);
    }
    const { data } = await api.post("/quote", payload);
    setQuote(data);
    setEmailed(false);
    return data;
  };

  const tipOpts = () => (tipAmount !== "" ? { tip_amount: Number(tipAmount) } : { tip_percent: tipPercent });

  const getQuote = async () => {
    if (!pickupCoord || !date) { toast.error("Please choose your pickup address from the dropdown suggestions and pick a date."); return; }
    if (service === "transfer" && !dropoffCoord) { toast.error("Please choose your drop-off address from the dropdown suggestions."); return; }
    setLoading(true);
    try {
      const dist = service === "transfer" ? await getRouteDistance() : { miles: null, duration: null };
      setDistCache(dist);
      await requote(dist, tipOpts(), true);
      toast.success("Instant quote ready.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const changeTipPercent = async (pct) => {
    setTipPercent(pct); setTipAmount("");
    if (quote) { try { await requote(distCache, { tip_percent: pct }); } catch { /* ignore */ } }
  };

  const changeTipAmount = async (val) => {
    setTipAmount(val);
    if (quote) {
      const opts = val === "" ? { tip_percent: tipPercent } : { tip_amount: Number(val) };
      try { await requote(distCache, opts); } catch { /* ignore */ }
    }
  };

  // Live pricing: auto-recalculate when address/service/vehicle/meet-greet/hours/promo change.
  const autoKey = JSON.stringify({
    p: pickupCoord, d: dropoffCoord, s: stops.map((x) => x.address).filter(Boolean),
    svc: service, veh: form.vehicle_type, mg: form.meet_greet, hrs: form.hours, promo: form.promo_code,
  });
  useEffect(() => {
    const ready = pickupCoord && (service === "hourly" ? Number(form.hours) > 0 : !!dropoffCoord);
    if (!ready) return;
    const t = setTimeout(async () => {
      try {
        const dist = service === "transfer" ? await getRouteDistance() : { miles: null, duration: null };
        setDistCache(dist);
        await requote(dist, tipOpts(), false);
      } catch { /* ignore */ }
    }, 700);
    return () => clearTimeout(t);
  }, [autoKey]);

  const proceed = () => {
    const pdt = new Date(`${format(date, "yyyy-MM-dd")}T${form.time}`);
    if (pdt - new Date() < 2 * 3600 * 1000) {
      toast.error("Reservations must be booked at least 2 hours before the pickup time. Please choose a later pickup time.");
      return;
    }
    navigate("/book", { state: { quote } });
  };

  return (
    <div data-testid="quote-form" className="bg-[#121212] border border-white/10 p-6 sm:p-8 shadow-[0_8px_40px_rgba(0,0,0,0.6)]">
      <div className="flex items-center gap-2 mb-5">
        <Sparkles size={18} className="text-primary" />
        <h3 className="text-xl font-heading text-foreground">Get Your Instant Quote</h3>
      </div>

      <Tabs value={service} onValueChange={(v) => { setService(v); setQuote(null); }} className="mb-6">
        <TabsList className="bg-secondary border border-white/10 rounded-none w-full grid grid-cols-2">
          <TabsTrigger value="transfer" data-testid="service-transfer" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground uppercase text-xs tracking-wide">Point-to-Point</TabsTrigger>
          <TabsTrigger value="hourly" data-testid="service-hourly" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground uppercase text-xs tracking-wide">Hourly Charter</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="grid gap-5 sm:grid-cols-2">
        <div className="space-y-2 sm:col-span-2">
          <Label className={lbl}>Pickup Location</Label>
          <AddressAutocomplete testId="pickup-input" value={form.pickup} placeholder="Enter pickup address"
            onChange={(v) => set("pickup", v)} onSelect={(p) => { set("pickup", p.address); setPickupCoord({ lat: p.lat, lng: p.lng }); }} />
        </div>

        {service === "transfer" && (
          <>
            {stops.map((s, i) => (
              <div key={i} className="space-y-2 sm:col-span-2">
                <Label className={lbl}>Stop {i + 1} <span className="text-primary normal-case tracking-normal">(+$10)</span></Label>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <AddressAutocomplete testId={`stop-input-${i}`} value={s.address} placeholder="Enter stop address"
                      onChange={(v) => updateStop(i, { address: v })} onSelect={(p) => updateStop(i, { address: p.address, coord: { lat: p.lat, lng: p.lng } })} />
                  </div>
                  <Button type="button" variant="outline" data-testid={`remove-stop-${i}`} onClick={() => removeStop(i)} className="border-white/10 rounded-none h-12 hover:text-red-400"><X size={16} /></Button>
                </div>
              </div>
            ))}
            <div className="space-y-2 sm:col-span-2">
              <Label className={lbl}>Drop-off Location</Label>
              <AddressAutocomplete testId="dropoff-input" value={form.dropoff} placeholder="Enter drop-off address"
                onChange={(v) => set("dropoff", v)} onSelect={(p) => { set("dropoff", p.address); setDropoffCoord({ lat: p.lat, lng: p.lng }); }} />
            </div>
            {stops.length < 3 && (
              <button type="button" data-testid="add-stop-btn" onClick={addStop} className="sm:col-span-2 flex items-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors">
                <Plus size={15} /> Add a stop (+$10 each)
              </button>
            )}
          </>
        )}

        {service === "hourly" && (
          <div className="space-y-2 sm:col-span-2">
            <Label className={lbl}>Duration (hours) — min 2</Label>
            <Select value={form.hours} onValueChange={(v) => set("hours", v)}>
              <SelectTrigger data-testid="hours-select" className={fieldCls}><Clock size={15} className="text-primary mr-1" /><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#121212] border-white/10 text-foreground">
                {[2,3,4,5,6,8,10,12].map((n) => <SelectItem key={n} value={String(n)}>{n} hours</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">Includes 20 free miles per hour. Extra miles billed at $2.75/mi based on actual trip.</p>
          </div>
        )}

        <div className="space-y-2">
          <Label className={lbl}>Date</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button data-testid="date-trigger" variant="outline" className={`${fieldCls} w-full justify-start font-normal`}>
                <CalendarIcon size={15} className="text-primary mr-2" />
                {date ? format(date, "PPP") : <span className="text-muted-foreground">Pick a date</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-[#121212] border-white/10" align="start">
              <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} initialFocus />
            </PopoverContent>
          </Popover>
        </div>
        <div className="space-y-2">
          <Label className={lbl}>Pickup Time</Label>
          <Input data-testid="time-input" type="time" value={form.time} onChange={(e) => set("time", e.target.value)} className={fieldCls} />
        </div>

        <div className="space-y-2">
          <Label className={lbl}>Vehicle Type</Label>
          <Select value={form.vehicle_type} onValueChange={changeVehicle}>
            <SelectTrigger data-testid="vehicle-select" className={fieldCls}><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#121212] border-white/10 text-foreground">
              {vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.name} · up to {v.pax}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className={lbl}>Passengers</Label>
          <Select value={form.passengers} onValueChange={(v) => set("passengers", v)}>
            <SelectTrigger data-testid="passengers-select" className={fieldCls}><Users size={15} className="text-primary mr-1" /><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#121212] border-white/10 text-foreground">
              {Array.from({ length: maxPax }, (_, i) => i + 1).map((n) => <SelectItem key={n} value={String(n)}>{n} {n === 1 ? "passenger" : "passengers"}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {service === "transfer" && (
          <div className="space-y-2">
            <Label className={lbl}>Flight # (optional)</Label>
            <Input data-testid="flight-input" placeholder="e.g. UA1234" value={form.flight_number} onChange={(e) => set("flight_number", e.target.value)} className={fieldCls} />
          </div>
        )}
        <div className={`space-y-2 ${service === "hourly" ? "sm:col-span-2" : ""}`}>
          <Label className={lbl}>Promo Code (optional)</Label>
          <Input data-testid="promo-input" placeholder="WELCOME10" value={form.promo_code} onChange={(e) => set("promo_code", e.target.value)} className={fieldCls} />
        </div>
      </div>

      <div className="flex items-center gap-3 mt-5">
        <Switch data-testid="meet-greet-switch" checked={form.meet_greet} onCheckedChange={(v) => set("meet_greet", v)} />
        <span className="text-sm text-foreground/80">Add Meet &amp; Greet — <span className="text-primary">+${meetFee}</span></span>
      </div>

      {quote && (
        <div data-testid="quote-result" className="mt-6 border border-primary/30 bg-primary/5 p-5">
          {quote.service_type === "hourly" ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3"><Clock size={14} className="text-primary" /> {quote.hours} hours · {quote.free_miles} free miles included</div>
          ) : quote.distance_miles ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3"><Route size={14} className="text-primary" /> {quote.distance_miles} mi{quote.duration_text ? ` · ${quote.duration_text}` : ""}{quote.num_stops ? ` · ${quote.num_stops} stop(s)` : ""}</div>
          ) : null}
          <div className="flex justify-between text-sm text-muted-foreground"><span>{quote.service_type === "hourly" ? `${quote.hours}h × $${quote.hourly_rate}/hr` : `Base fare (${quote.vehicle_name})`}</span><span className="text-foreground">${quote.base_fare.toFixed(2)}</span></div>
          {quote.meet_greet_fee > 0 && <div className="flex justify-between text-sm text-muted-foreground mt-1"><span>Meet &amp; Greet</span><span className="text-foreground">${quote.meet_greet_fee.toFixed(2)}</span></div>}
          {quote.tip > 0 && <div className="flex justify-between text-sm text-muted-foreground mt-1"><span>Tip {quote.tip_custom ? "(custom)" : `(${Math.round(quote.tip_percent)}%)`}</span><span className="text-foreground">${quote.tip.toFixed(2)}</span></div>}
          {quote.discount > 0 && <div className="flex justify-between text-sm mt-1"><span className="text-primary">Promo {quote.promo_applied}</span><span className="text-primary">−${quote.discount.toFixed(2)}</span></div>}

          <div className="mt-4 pt-4 border-t border-white/10">
            <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-2">Add a tip for your chauffeur</div>
            <div className="flex flex-wrap items-center gap-2" data-testid="tip-options">
              {[10, 20, 30].map((p) => (
                <button key={p} type="button" data-testid={`tip-${p}`} onClick={() => changeTipPercent(p)}
                  className={`px-3 py-1.5 text-sm border rounded-none transition-colors ${tipAmount === "" && tipPercent === p ? "border-primary bg-primary text-primary-foreground" : "border-white/15 text-foreground/80 hover:border-primary"}`}>
                  {p}%
                </button>
              ))}
              <div className={`flex items-center border rounded-none ${tipAmount !== "" ? "border-primary" : "border-white/15"}`}>
                <span className="pl-2 text-sm text-muted-foreground">$</span>
                <input type="number" min="0" step="1" data-testid="tip-custom" value={tipAmount} placeholder="Custom"
                  onChange={(e) => changeTipAmount(e.target.value)}
                  className="w-20 px-1.5 py-1.5 text-sm bg-transparent text-foreground rounded-none focus:outline-none" aria-label="Custom tip amount in dollars" />
              </div>
              <button type="button" data-testid="tip-none" onClick={() => changeTipPercent(0)}
                className={`px-3 py-1.5 text-sm border rounded-none transition-colors ${tipAmount === "" && tipPercent === 0 ? "border-primary bg-primary text-primary-foreground" : "border-white/15 text-foreground/80 hover:border-primary"}`}>
                No tip
              </button>
            </div>
          </div>

          <div className="flex justify-between items-end mt-3 pt-3 border-t border-white/10">
            <span className="text-sm uppercase tracking-wide text-muted-foreground">Total{quote.service_type === "hourly" ? " (est.)" : ""}</span>
            <span data-testid="quote-total" className="text-3xl font-heading text-primary">${quote.total.toFixed(2)}</span>
          </div>
        </div>
      )}

      <div className="mt-6 flex flex-col sm:flex-row gap-3">
        <Button data-testid="get-quote-btn" onClick={getQuote} disabled={loading}
          className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 h-12 uppercase tracking-wide rounded-none">
          {loading ? "Calculating..." : "Get Instant Quote"}
        </Button>
        {quote && (
          <Button data-testid="proceed-book-btn" onClick={proceed}
            className="flex-1 bg-transparent border border-primary text-primary hover:bg-primary hover:text-primary-foreground h-12 uppercase tracking-wide rounded-none">
            Reserve &amp; Pay <ArrowRight size={16} className="ml-2" />
          </Button>
        )}
      </div>

      {quote && (
        emailed ? (
          <p className="mt-4 flex items-center justify-center gap-2 text-sm text-primary" data-testid="email-sent-msg">
            <Check size={16} /> Quote sent to your inbox — check your email to finish booking anytime.
          </p>
        ) : (
          <div className="mt-4 flex gap-2" data-testid="email-quote-row">
            <Input type="email" data-testid="lead-email-input" placeholder="Email me this quote" value={leadEmail}
              onChange={(e) => setLeadEmail(e.target.value)} className={`${fieldCls} flex-1`} />
            <Button type="button" data-testid="email-quote-btn" onClick={emailQuote}
              className="bg-transparent border border-white/20 text-foreground hover:border-primary hover:text-primary rounded-none h-12 px-4">
              <Mail size={16} className="mr-2" /> Send
            </Button>
          </div>
        )
      )}
    </div>
  );
}
